'use client'

import { useCallback, useRef, useState } from 'react'

import { NewObject } from './object'
import { useMultiScrollState } from './scroll'

const TAB_HEIGHT = 44

type Props<T extends NewObject> = {
  defaultValue: T[keyof T]
  tabs: { label: string; value: T[keyof T] }[]
  tabHeight?: number
  offset?: number
}

export const useTab = <T extends NewObject>({
  defaultValue,
  tabs,
  tabHeight = TAB_HEIGHT,
  offset = 0,
}: Props<T>) => {
  const [activeTab, setActiveTab] = useState<T[keyof T]>(defaultValue)

  // 스크롤 이벤트 핸들러에 의존성을 추가하지 않고 현재 활성화된 탭을 가져오기 위해 사용
  const lastActiveTabRef = useRef<T[keyof T]>(defaultValue)

  const sectionsRef = useRef<Array<{ tab: T[keyof T]; top: number }>>([])

  const { getElement } = useMultiScrollState()

  const getSectionTop = useCallback((tab: T[keyof T]) => {
    const section = sectionsRef.current.find(section => section.tab === tab)

    return section?.top || Infinity
  }, [])

  const updateActiveTab = useCallback(
    (tab: T[keyof T]) => {
      if (tab !== lastActiveTabRef.current) {
        lastActiveTabRef.current = tab
        setActiveTab(tab)
      }
    },
    [setActiveTab],
  )

  const updateSections = useCallback(() => {
    const newSections = tabs.map(tab => {
      const element = getElement(tab.value as string)

      if (!element) return { tab: tab.value, top: Infinity }

      const rect = element.getBoundingClientRect()

      return {
        tab: tab.value,
        top: rect.top + window.scrollY - (tabHeight + offset),
      }
    })

    sectionsRef.current = newSections
  }, [getElement])

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY

    const sections = sectionsRef.current

    const newActiveTab =
      sections.findLast(section => {
        return currentScrollY >= section.top
      })?.tab || defaultValue

    updateActiveTab(newActiveTab)
  }, [updateActiveTab, defaultValue])

  return {
    activeTab,
    setActiveTab: updateActiveTab,
    getSectionTop,
    updateSections,
    handleScroll,
  }
}
